import {
  Flex,
  IconButton,
  IconEditLine,
  IconTrashLine,
  InlineList,
  Text,
  ToggleDetails,
  Tooltip,
  View,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useContext, useState } from "react";
import { ReviewService } from "../../../../services/ReviewService";
import { sort } from "../../../../utils/utils";
import { Card } from "../../../Card";
import {
  ERROR,
  useNotification,
} from "../../../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../../../DialogYesNo/DialogYesNo";
import { ReviewCycleContext } from "../Review";
import { isCycleActive } from "./utils";

const CourseReviewCycleEntry = ({ cycle, setUpsertCycle, setEditingCycle }) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const { reflectionTemplates, setCourseReviewCycles } =
    useContext(ReviewCycleContext);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const isActive = isCycleActive(cycle);

  async function handleDeleteCycle() {
    try {
      setDeleting(true);
      await ReviewService.deleteReviewCycle(cycle.id);
      setCourseReviewCycles((prevCycles) =>
        prevCycles.filter((c) => c.id !== cycle.id)
      );
      showNotification(
        t("notifications.general.deleteSuccess", {
          obj: t("courseReviewCycle.courseReviewCycle"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.deleteError", {
          obj: t("courseReviewCycle.courseReviewCycle"),
        }),
        ERROR
      );
    } finally {
      setDeleting(false);
    }
  }

  function handleEditCycle(cycle) {
    setEditingCycle({
      id: cycle.id,
      duration: cycle.duration || 1,
      startYear: cycle.start_date,
      courses: cycle.reviewables,
      reflectionTemplateId: cycle.reflection_template_id,
      notify_faculty_prior_term_end_days:
        cycle.notify_faculty_prior_term_end_days,
      notify_faculty_past_term_end_days:
        cycle.notify_faculty_past_term_end_days,
    });
  }

  return (
    <Card
      data-testid="course-review-cycle-entry"
      style={{
        color: "#0D323F",
        backgroundColor: "#F2F8FA",
        border: "1px solid #ccc",
        borderRadius: 10,
        padding: "10px 15px",
      }}
    >
      <Flex gap="small">
        <Flex.Item shouldGrow shouldShrink>
          <Text as="div" weight="bold" data-testid="cycle-period">
            {cycle.duration}
            {cycle.duration == 1
              ? t("courseReviewCycle.year")
              : t("courseReviewCycle.years")}
            , {t("courseReviewCycle.starting")} {cycle.start_date.name}
          </Text>
          <View as="div" margin="xx-small 0 0 0">
            <Text color="brand" data-testid="reflection-template-name">
              {t("reflectionTemplates.reflectionTemplate")}:{" "}
              {reflectionTemplates.find(
                (t) => t.id === cycle.reflection_template_id
              )?.name || t("global.none")}
            </Text>
          </View>
          <View as="div" margin="xx-small 0 0 0">
            <ToggleDetails
              data-testid="courses-toggle"
              summary={
                <Text color="brand">
                  {t("programs.courses")}: {cycle.reviewables.length}
                </Text>
              }
            >
              <InlineList
                delimiter="pipe"
                size="small"
                data-testid="courses-list"
              >
                {sort(
                  cycle.reviewables,
                  (course) => course.name || course.code
                ).map((course) => (
                  <InlineList.Item key={course.ddl_id || course.id}>
                    {course.name || course.code}
                  </InlineList.Item>
                ))}
              </InlineList>
            </ToggleDetails>
          </View>
        </Flex.Item>
        <Flex.Item>
          <IconButton
            screenReaderLabel={`${t("academicHierarchy.edit")} ${t(
              "courseReviewCycle.courseReviewCycle"
            )}`}
            renderIcon={IconEditLine}
            size="small"
            onClick={() => {
              setUpsertCycle(true);
              handleEditCycle(cycle);
            }}
            disabled={deleting}
          />
        </Flex.Item>
        <Flex.Item>
          {isActive ? (
            <Tooltip
              renderTip={t("courseReviewCycle.cannotDeleteActiveCycle")}
              color="primary-inverse"
            >
              <IconButton
                screenReaderLabel={`${t("evidence.delete")} ${t(
                  "courseReviewCycle.courseReviewCycle"
                )}`}
                renderIcon={IconTrashLine}
                size="small"
                disabled
              />
            </Tooltip>
          ) : (
            <IconButton
              screenReaderLabel={`${t("evidence.delete")} ${t(
                "courseReviewCycle.courseReviewCycle"
              )}`}
              renderIcon={IconTrashLine}
              size="small"
              onClick={() => setDeleteConfirm(true)}
              disabled={deleting}
            />
          )}
          <DialogYesNo
            message={t("courseReviewCycle.deleteConfirm")}
            openStatus={deleteConfirm}
            onClickNo={() => setDeleteConfirm(false)}
            onClickYes={handleDeleteCycle}
          />
        </Flex.Item>
      </Flex>
    </Card>
  );
};

export default CourseReviewCycleEntry;
